import React, { useEffect, useRef, useState,useCallback } from "react";
import { connect } from "react-redux";
import {
    DataGrid,
    Column,
    Editing,
    Scrolling,
    Selection,
    Sorting,
    Toolbar,
    Item as ToolbarItem,
    LoadPanel as GridLoadPanel,
} from "devextreme-react/data-grid";
import { Button } from "devextreme-react/button";
import { LoadPanel } from 'devextreme-react/load-panel';
import routes from "../../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../../common/Messages";
import { alert, confirm } from 'devextreme/ui/dialog';
import { GetFormatedDateWithTime } from '../../../../../../common/Utils';
import { WarrantyFlagShipment } from '../../../../../../enum/Enum';
import { collectShipExpirationRejections } from './shipExpirationCheck';
function ManuallySelectSublotForShipment(props) {
    const accessToken = props.oidc.user.access_token;
    const [gridData, setGridData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const position = { of: '#accordion-containe2' };
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const [showIndicator, setShowIndicator] = useState(true);
    const [hideOnOutsideClick, setHideOnOutsideClick] = useState(false);
    const [shading, setShading] = useState(true);
    const [showPane, setShowPane] = useState(true);
    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, [setLoadPanelVisible]);
    const gridRef = useRef(null);
    // Extracted so the effect below can declare them as dependencies without the linter treating
    // optional chaining as a complex expression. These three feed the server-side ship date.
    const transactionDate = props.formFields?.TRANSACTIONDATE;
    const actualReceivedShippedDateTime = props.formFields?.ACTUALRECEIVEDSHIPPEDDATETIME;
    const ignoreSameDayShipTime = props.profile?.Ignoresamedayshiptime;
    const itemRowId = props.itemRowId;
    const lotRowId = props.lotRowId;

    useEffect(() => {
        let stale = false;
        const fetchData = async () => {
            setIsLoading(true);
            try {
                if (!itemRowId || !lotRowId) { setGridData([]); return; }
                const response = await fetch(
                    `${window._env_.REACT_APP_APIS}/${routes.IM.GetLocationQuantitySummaryByItemOrLot}?itemId=${itemRowId}&lotId=${lotRowId}&transactionDate=${GetFormatedDateWithTime(transactionDate)}&actualReceivedShippedDateTime=${GetFormatedDateWithTime(actualReceivedShippedDateTime)}&ignoreSameDayShipTime=${ignoreSameDayShipTime}`,
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                );
                if (!response.ok) throw new Error(MESSAGES.ERROR);
                const data = await response.json();
                if (!stale) setGridData(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error(error);
                if (!stale) setGridData([]);
            } finally {
                // Guarded so a superseded request cannot clear the flag while the current one is
                // still in flight - that is what made the spinner vanish early.
                if (!stale) setIsLoading(false);
            }
        };

        fetchData();
        return () => { stale = true; };
    }, [itemRowId, lotRowId, transactionDate, actualReceivedShippedDateTime, ignoreSameDayShipTime, accessToken]);

    // The grid's own load panel, driven imperatively. beginCustomLoading keeps it up until
    // endCustomLoading is called and suppresses the "no data" text meanwhile, which a controlled
    // <LoadPanel visible={...}> could not do reliably - the widget desynced from its prop.
    useEffect(() => {
        const grid = gridRef.current?.instance?.();
        if (!grid) return;
        if (isLoading) grid.beginCustomLoading(''); else grid.endCustomLoading();
    }, [isLoading]);
    // Shipment expiration / warranty check, run as each checkbox is toggled and clearing the tick when
    // the row is refused - the same point and the same effect as Delphi (uWMD0107.pas:487-522, which
    // sets Selected := 0). The server re-checks on save as the backstop.
    const handleSelectionChanged = async (e) => {
        const newlySelected = e.currentSelectedRowKeys?.length
            ? e.selectedRowsData.filter((row) => e.currentSelectedRowKeys.includes(row.rowid))
            : [];

        if (!newlySelected.length) return;

        const rejected = await collectShipExpirationRejections({
            rows: newlySelected,
            keyOf: (row) => row.rowid,
            labelOf: (row) => row.Sublot,
            expiryOf: (row) => row.ExpiryDate,
            shipDate: actualReceivedShippedDateTime ?? transactionDate,
            expectedFlag: WarrantyFlagShipment.UseSublotExpirationDate,
        });

        // No Reserve Location class check here: Delphi gates that rule on receipts
        // (uWMD0002.pas:12896) and this screen is shipment-only.
        if (rejected.length)
            e.component.deselectRows(rejected);
    };

    // The expiration approval was already collected per row above, so the save posts IgnoreWarnings
    // = true and the server only blocks a hard stop.
    const postSelection = async (selected) => {
        setLoadPanelVisible(true);
        // One row per picked location: the same sublot can be picked from several locations, and the
        // API groups them into one sub-detail while keeping a location row each (legacy WMD0107).
        const payload = selected.map((row) => {
            const qty = row.ShippedQuantity ?? 0;
            return {
                RTransactiondetail: props.rTransactiondetail,
                RTransactionHeader: props.formFields?.ROWID,
                RItem: props.itemRowId,
                RLot: row.RLot ?? props.lotRowId,
                RSublot: row.RSublot,
                Quantityactual: qty,
                Quantityorder: qty,
                OutShipped: qty,
                RLocation: row.RLocation,
                RWarehouse: row.RWarehouse,
                IgnoreWarnings: true,
            };
        });
        try {
            const response = await fetch(
                `${window._env_.REACT_APP_APIS}/${routes.WM.AddBatchTransactionSubDetails}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(payload),
                }
            );
            setLoadPanelVisible(false);
            if (!response.ok) {
                let body = null;
                try {
                    body = await response.json();
                } catch (parseError) {
                    body = null;
                }
                return {
                    saved: false,
                    message: body?.message ?? MESSAGES.ERROR,
                    statusCode: body?.statusCode,
                };
            }
            const result = await response.json();
            if (!result.success) {
                return { saved: false, message: result.message };
            }
            alert(MESSAGES.CREATE_SUCCESS, MESSAGES.INFORMATION, 2000);
            props.handleManuallySelectSublotForShipmentSave();
            return { saved: true };
        } catch (error) {
            console.error(error.message, error);
            setLoadPanelVisible(false);
            return { saved: false, message: error.message };
        }
    };

    const handleBatchSave = async () => {
        const grid = gridRef.current?.instance();
        await grid.saveEditData();
        const rows = grid.getVisibleRows().map((r) => r.data);
        const selected = grid.getSelectedRowsData();
        if (!selected.length) {
            alert(MESSAGES.NO_ROW_SELECTED, MESSAGES.ERROR, 2000);
            return;
        }
        const overAvailable = selected.find(
            (row) => (row.ShippedQuantity ?? 0) > (row.Available ?? 0)
        );
        if (overAvailable) {
            alert(MESSAGES.ACTUAL_QUANTITY_GREATER_THEN_AVAILABLE, MESSAGES.ERROR);
            return;
        }
        // Delphi asks for confirmation when every row was selected (uWMD0107.pas:1184).
        if (rows.length > 0 && selected.length === rows.length) {
            const proceed = await confirm(
                `<i>${MESSAGES.ALL_RECORDS_SELECTED_CONFIRM}</i>`,
                MESSAGES.CONFIRM
            );
            if (!proceed) return;
        }
        const result = await postSelection(selected);
        if (!result.saved)
            alert(result.message, MESSAGES.ERROR);
    };
    const handleCancel = () => {
        props.CloseManualShipmentScreen(false);
    };
    return (
        <div style={{ position: "relative", paddingBottom: "10px" }}>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                position={position}
                onHiding={hideLoadPanel}
                visible={loadPanelVisible}
                showIndicator={showIndicator}
                shading={shading}
                showPane={showPane}
                hideOnOutsideClick={hideOnOutsideClick}
            />
            <DataGrid

onExporting={(e) => {
          if (window.handleDevExtremeDataGridExport) {
            window.handleDevExtremeDataGridExport(e);
          }
        }}
                id="gridSubLotContainer"
                ref={gridRef}
                dataSource={gridData}
                keyExpr="rowid"
                showBorders={true}
                rowAlternationEnabled={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                height="55vh"
                repaintChangesOnly={true}
                onSelectionChanged={handleSelectionChanged}
            >
                {/* Shown for exactly as long as the store's load promise is pending. */}
                <GridLoadPanel enabled={true} />
                <Scrolling mode="standard" />
                <Selection mode="multiple" />
                <Sorting mode="multiple" />
                <Editing mode="batch" allowUpdating={true} />
                {/* Column order and captions follow the Delphi grid (uWMD0107.dfm). */}
                <Column dataField="Sublot" caption="Sublot" allowEditing={false} sortIndex={0} sortOrder="asc" />
                <Column dataField="SublotDescription" caption="Sublot Description" allowEditing={false} />
                <Column dataField="AvailableDate" caption="Available Date" dataType="datetime" format="MM/dd/yyyy" allowEditing={false} />
                <Column dataField="ExpiryDate" caption="Expiration Date" dataType="datetime" format="MM/dd/yyyy" allowEditing={false} />
                <Column dataField="warehouse" caption="Warehouse" allowEditing={false} />
                <Column dataField="LocationCode" caption="Location" allowEditing={false} />
                <Column dataField="Available" caption="Available" dataType="number" format="#,##0.00" allowEditing={false} />
                <Column
                    dataField="ShippedQuantity"
                    caption="Ship Quantity"
                    dataType="number" format="#,##0.00"
                    setCellValue={(newData, value, currentRowData) => {
                        // Delphi warns and then clamps the entry back to Available - it does not just
                        // reject the edit (uWMD0107.pas:1235-1239 for lots, :531-535 for sublots).
                        // The server re-checks as well, since a direct API call bypasses this.
                        if (value > currentRowData.Available) {
                            alert(MESSAGES.ACTUAL_QUANTITY_GREATER_THEN_AVAILABLE, MESSAGES.ERROR);
                            newData.ShippedQuantity = currentRowData.Available;
                            return;
                        }
                        newData.ShippedQuantity = value;
                    }}
                />
            </DataGrid>
            <div
                style={{
                    position: "fixed",
                    bottom: "20px",
                    right: "20px",
                    display: "flex",
                    gap: "10px",
                    zIndex: 1000,
                }}
            >
                <Button
                    text="Ok"
                    type="default"
                    stylingMode="contained"
                    onClick={handleBatchSave}
                    disabled={isLoading || !gridData.length}
                />
                <Button
                    text="Cancel"
                    stylingMode="outlined"
                    onClick={handleCancel}
                />
            </div>

        </div>
    );
}

function mapStateToProps(state) {
    return {
        ReceiptReducer: state.ReceiptReducer,
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(ManuallySelectSublotForShipment);
